/*
 * The ":" source (spec 12 §3.5, P21, F-2 §11.4). Typing `:tada` offers 🎉. In the source view
 * inserting it writes the *shortcode*: `:tada:` is what the author sees in their own markdown, what
 * an edit shows them again, and what the server resolves with the same GitHub preset the table came
 * from — so the composer can never promise a shortcode the message would not render. The formatted
 * editor takes `insertRich` instead and gets the character at the person's skin tone, because what
 * that surface shows is the result and not the markup; the server renders both the same.
 *
 * The source belongs to the composer and not to a surface: unlike "@" and "#" it depends on no
 * conversation, no membership and no store. `useAutocomplete` therefore adds it to whatever sources
 * a surface hands in, which is why the ":" trigger works in every composer — the conversation, the
 * in-place edit, a thread later — without a single one of them wiring it.
 *
 * It inserts at the tone the picker chose (`recents.ts`), so a person who picked a tone once gets
 * it from both doors.
 */

import { MAX_ITEMS } from "../composer/autocomplete/trigger.ts"
import type { AutocompleteItem, AutocompleteSource } from "../composer/types.ts"
import { emojiSkinTone } from "./recents.ts"
import { loadEmojiTable, withSkinTone } from "./table.ts"

/**
 * The item id is the shortcode and the label the character, so the list reads as a column of emoji
 * with their names — the shortcode is the detail, because that is what will be written.
 */
export const emojiSource: AutocompleteSource = {
	trigger: ":",
	// One character: ":" alone would open a list of every emoji there is under a colon that is far
	// more often the start of a time or a URL.
	minChars: 2,
	async query(term: string): Promise<AutocompleteItem[]> {
		const table = await loadEmojiTable()
		const tone = emojiSkinTone()
		return table.search(term, MAX_ITEMS).map((entry) => {
			const char = withSkinTone(entry, tone)
			return {
				id: entry.shortcodes[0] ?? entry.char,
				label: `${char} ${entry.label}`,
				detail: entry.shortcodes[0] === undefined ? entry.char : `:${entry.shortcodes[0]}:`,
				char,
			}
		})
	},
	insert(item: AutocompleteItem): string {
		return `:${item.id}: `
	},
	insertRich(item: AutocompleteItem): string {
		return `${item.char ?? `:${item.id}:`} `
	},
}

/**
 * The sources a composer actually queries: the surface's own, plus the emoji source unless the
 * surface brought one of its own. A trigger the composer declares and nothing answers is a key a
 * person presses into silence, and ":" is declared in `types.ts`.
 */
export function withEmojiSource(
	sources: readonly AutocompleteSource[],
): readonly AutocompleteSource[] {
	return sources.some((source) => source.trigger === ":") ? sources : [...sources, emojiSource]
}
